import React, { useEffect, useState } from 'react';
import { DashboardMetricsAPI } from '../../../../api/DashboardMetrics';

const MAX_CHANNELS = 8;
const MAX_LABEL = 40;

/* Valida la configuración de la tabla consolidada. Devuelve la lista de problemas (vacía si es válida).
   El servidor repite estas reglas; esto solo evita guardar algo que será rechazado. */
export const validateConsolidatedConfig = (config) => {
  const problems = [];
  const series = config.series || [];
  if (series.length > MAX_CHANNELS) problems.push(`Máximo ${MAX_CHANNELS} canales`);
  const labels = new Set();
  const machines = new Set();
  series.forEach((channel, index) => {
    const label = (channel.label || '').trim();
    if (!label) problems.push(`El canal ${index + 1} necesita un nombre`);
    if (label.length > MAX_LABEL) problems.push(`El nombre del canal ${index + 1} supera ${MAX_LABEL} caracteres`);
    if (label && labels.has(label.toLowerCase())) problems.push(`El canal «${label}» está repetido`);
    labels.add(label.toLowerCase());
    if (!channel.machine_ids || channel.machine_ids.length === 0) problems.push(`El canal «${label || index + 1}» necesita al menos un tipo de máquina`);
    (channel.machine_ids || []).forEach((id) => {
      if (machines.has(id)) problems.push(`Un tipo de máquina está en más de un canal`);
      machines.add(id);
    });
  });
  return Array.from(new Set(problems));
};

/* Configuración de la tabla de ventas consolidadas: qué es cada fila y cómo se agrupan los canales de venta.
   Los canales son grupos de tipos de máquina (los de la tabla de máquinas); el nombre lo pone el usuario. */
export default function ConsolidatedConfig({ value, onChange }) {
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    DashboardMetricsAPI.getConsolidatedOptions(localStorage.getItem('x-access-token'))
      .then((response) => {
        if (cancelled) return;
        const data = response?.data?.data;
        setOptions(data);
        // First time: start from the default channels
        if (data && (!value.series || value.series.length === 0)) onChange({ ...value, series: data.default_series.map((item) => ({ label: item.label, machine_ids: [...item.machine_ids] })) });
      })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.error || 'No se pudieron cargar las opciones de la tabla'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <div role="alert" style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>;
  if (!options) return <div role="status" style={{ fontSize: 13, color: '#6b6b66' }}>Cargando opciones…</div>;

  const series = value.series || [];
  const set = (patch) => onChange({ ...value, ...patch });
  const setChannel = (index, patch) => set({ series: series.map((channel, i) => (i === index ? { ...channel, ...patch } : channel)) });
  const usedBy = (machineId) => series.findIndex((channel) => channel.machine_ids.includes(machineId));
  const toggleMachine = (index, machineId) => {
    const channel = series[index];
    const has = channel.machine_ids.includes(machineId);
    setChannel(index, { machine_ids: has ? channel.machine_ids.filter((id) => id !== machineId) : [...channel.machine_ids, machineId] });
  };
  const problems = validateConsolidatedConfig(value);
  const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 };
  const input = { padding: '7px 9px', border: '1px solid rgba(0,0,0,0.14)', borderRadius: 7, fontFamily: 'inherit', fontSize: 13 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="consolidated-config">
      <p style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.5, margin: 0 }}>
        Tabla de ventas netas (devoluciones restadas, sin propinas) por canal. Respeta las sucursales, grupos, eventos y fechas del Overview.
      </p>

      <label style={field}>
        Cada fila es una…
        <select style={input} value={value.rows || 'date'} onChange={(event) => set({ rows: event.target.value })}>
          {options.rows.map((row) => <option key={row.id} value={row.id}>{row.label}</option>)}
        </select>
      </label>

      {(value.rows || 'date') === 'date' && (
        <>
          <label style={field}>
            Período de cada fila
            <select style={input} value={value.group_by || 'day'} onChange={(event) => set({ group_by: event.target.value })}>
              {options.group_by.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label style={{ ...field, flexDirection: 'row', alignItems: 'center', fontWeight: 500 }}>
            <input type="checkbox" checked={Boolean(value.cumulative)} onChange={(event) => set({ cumulative: event.target.checked })} />
            Mostrar una columna de acumulado
          </label>
        </>
      )}
      {(value.rows || 'date') === 'group' && (
        <p style={{ fontSize: 12, color: '#92400e', margin: 0 }}>Usa los grupos que selecciones en el filtro «Grupos» del Overview.</p>
      )}

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Canales de venta</div>
        <p style={{ fontSize: 12, color: '#6b6b66', margin: '0 0 8px' }}>
          Cada canal agrupa tipos de máquina; un tipo solo puede estar en un canal. Lo que no esté en ningún canal aparece como «Otros».
        </p>
        {series.map((channel, index) => (
          <fieldset key={index} style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
            <legend style={{ fontSize: 11, color: '#6b6b66' }}>Canal {index + 1}</legend>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <input
                style={{ ...input, flex: 1 }} aria-label={`Nombre del canal ${index + 1}`} value={channel.label} maxLength={MAX_LABEL}
                onChange={(event) => setChannel(index, { label: event.target.value })}
              />
              <button type="button" onClick={() => set({ series: series.filter((_, i) => i !== index) })} style={{ ...input, cursor: 'pointer' }}>Quitar</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
              {options.machine_types.map((machine) => {
                const owner = usedBy(machine.id);
                const takenByOther = owner !== -1 && owner !== index;
                return (
                  <label key={machine.id} style={{ fontSize: 12, opacity: takenByOther ? 0.45 : 1, display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label={`${machine.name} en el canal ${index + 1}`}
                      checked={channel.machine_ids.includes(machine.id)}
                      disabled={takenByOther}
                      onChange={() => toggleMachine(index, machine.id)}
                    />
                    {machine.name}
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" disabled={series.length >= MAX_CHANNELS} onClick={() => set({ series: [...series, { label: '', machine_ids: [] }] })} style={{ ...input, cursor: 'pointer' }}>
            + Agregar canal
          </button>
          <button type="button" onClick={() => set({ series: options.default_series.map((item) => ({ label: item.label, machine_ids: [...item.machine_ids] })) })} style={{ ...input, cursor: 'pointer' }}>
            Restablecer canales
          </button>
        </div>
      </div>

      {problems.length > 0 && (
        <ul role="alert" style={{ color: '#dc2626', fontSize: 12, margin: 0, paddingLeft: 18 }}>
          {problems.map((problem) => <li key={problem}>{problem}</li>)}
        </ul>
      )}
    </div>
  );
}
